import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Check if this quantity selector is for a delivery product
   * @returns {boolean}
   */
  isDeliveryProduct() {
    const quantityControls = this.closest('.cart-items__quantity-controls[data-delivery-product="true"]');
    return quantityControls !== null;
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // If this is a delivery product, always disable buttons
    if (this.isDeliveryProduct()) {
      minusButton.disabled = true;
      plusButton.disabled = true;
      return;
    }

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Handles the quantity increase event.
   * Prevents increase for delivery products.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (this.isDeliveryProduct()) {
      if (event) event.preventDefault();
      return; // Prevent quantity increase for delivery products
    }
    super.increaseQuantity(event);
  }

  /**
   * Handles the quantity decrease event.
   * Prevents decrease for delivery products.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (this.isDeliveryProduct()) {
      if (event) event.preventDefault();
      return; // Prevent quantity decrease for delivery products
    }
    super.decreaseQuantity(event);
  }

  /**
   * Handles setting quantity from input.
   * Prevents manual input changes for delivery products.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (this.isDeliveryProduct()) {
      if (event) event.preventDefault();
      // Reset to original value if user tries to change it
      const { quantityInput } = this.refs;
      if (quantityInput && quantityInput instanceof HTMLInputElement) {
        const originalValue = quantityInput.getAttribute('data-cart-quantity') || quantityInput.defaultValue;
        quantityInput.value = originalValue;
      }
      return;
    }
    super.setQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
